import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  Brain, 
  Lightbulb,
  CheckCircle,
  AlertTriangle,
  TrendingUp,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  ThumbsUp,
  ThumbsDown,
  BookOpen,
  MessageSquare,
  Star,
  Zap,
  Plus,
  Check,
  X,
  Play
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LearningPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedReview, setExpandedReview] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [newSuggestionOpen, setNewSuggestionOpen] = useState(false);
  const [newSuggestion, setNewSuggestion] = useState({ topic: '', content: '', action: 'add', priority: 'medium' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, reviewsRes, suggestionsRes] = await Promise.all([
        axios.get(`${API}/learning/stats`),
        axios.get(`${API}/avery/reviews?limit=20`),
        axios.get(`${API}/learning/suggestions?limit=50`)
      ]);
      setStats(statsRes.data);
      setReviews(reviewsRes.data.reviews);
      setSuggestions(suggestionsRes.data.suggestions);
    } catch (error) {
      console.error('Failed to fetch learning data:', error);
    } finally {
      setLoading(false);
    }
  };

  const runAveryAnalysis = async () => {
    setAnalyzing(true);
    try {
      const response = await axios.post(`${API}/avery/analyze?limit=20`);
      toast.success(`Analysis complete! Reviewed ${response.data.analyzed} conversations`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleReviewAction = async (reviewId, action) => {
    try {
      await axios.post(`${API}/avery/reviews/${reviewId}/action?action=${action}`);
      toast.success(`Review ${action}ed`);
      fetchData();
    } catch (error) {
      toast.error('Action failed');
    }
  };

  const handleSuggestionAction = async (suggestionId, action) => {
    try {
      await axios.post(`${API}/learning/suggestions/${suggestionId}/action?action=${action}`);
      toast.success(`Suggestion ${action}ed`);
      fetchData();
    } catch (error) {
      toast.error('Action failed');
    }
  };

  const createSuggestion = async () => {
    try {
      const formData = new FormData();
      formData.append('topic', newSuggestion.topic);
      formData.append('content', newSuggestion.content);
      formData.append('action', newSuggestion.action);
      formData.append('priority', newSuggestion.priority);
      
      await axios.post(`${API}/learning/suggestions`, formData);
      toast.success('Suggestion created');
      setNewSuggestionOpen(false);
      setNewSuggestion({ topic: '', content: '', action: 'add', priority: 'medium' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create suggestion');
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString('en-CA', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-500/10 text-red-400 border-red-500/30';
      case 'high': return 'bg-orange-500/10 text-orange-400 border-orange-500/30';
      case 'medium': return 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30';
      case 'low': return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30';
      default: return 'bg-gray-500/10 text-gray-400 border-gray-500/30';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending': return <Badge variant="outline" className="bg-yellow-500/10 text-yellow-400">Pending</Badge>;
      case 'approved': return <Badge variant="outline" className="bg-blue-500/10 text-blue-400">Approved</Badge>;
      case 'implemented': return <Badge variant="outline" className="bg-emerald-500/10 text-emerald-400">Implemented</Badge>;
      case 'rejected': return <Badge variant="outline" className="bg-red-500/10 text-red-400">Rejected</Badge>;
      case 'reviewed': return <Badge variant="outline" className="bg-gray-500/10 text-gray-400">Reviewed</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-foreground flex items-center gap-2">
            <Brain className="w-8 h-8 text-primary" />
            Learning System
          </h1>
          <p className="text-muted-foreground mt-1">
            Avery QA Agent & Knowledge Base Improvements
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchData} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={runAveryAnalysis} disabled={analyzing}>
            {analyzing ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
            ) : (
              <Play className="w-4 h-4 mr-2" />
            )}
            Run Avery Analysis
          </Button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-border">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'overview' 
              ? 'border-primary text-primary' 
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <TrendingUp className="w-4 h-4 inline mr-2" />
          Overview
        </button>
        <button
          onClick={() => setActiveTab('reviews')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'reviews' 
              ? 'border-primary text-primary' 
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <Brain className="w-4 h-4 inline mr-2" />
          Avery Reviews
          {stats?.avery?.pending_reviews > 0 && (
            <Badge variant="outline" className="ml-2 bg-primary/10 text-primary">
              {stats.avery.pending_reviews}
            </Badge>
          )}
        </button>
        <button
          onClick={() => setActiveTab('suggestions')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'suggestions' 
              ? 'border-primary text-primary' 
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <Lightbulb className="w-4 h-4 inline mr-2" />
          KB Suggestions
          {stats?.suggestions?.pending > 0 && (
            <Badge variant="outline" className="ml-2 bg-primary/10 text-primary">
              {stats.suggestions.pending}
            </Badge>
          )}
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && stats && (
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Conversations Card */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Conversations</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-foreground">{stats.conversations.total}</p>
              <div className="mt-2 space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Reviewed</span>
                  <span className="text-foreground">{stats.conversations.reviewed}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Corrected</span>
                  <span className="text-foreground">{stats.conversations.corrected}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-yellow-400">Flagged</span>
                  <span className="text-yellow-400">{stats.conversations.flagged}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Avery Reviews Card */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Brain className="w-4 h-4 text-primary" />
                Avery Reviews
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-foreground">{stats.avery.total_reviews}</p>
              <div className="mt-2 space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-yellow-400">Pending</span>
                  <span className="text-yellow-400">{stats.avery.pending_reviews}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* KB Suggestions Card */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-yellow-400" />
                KB Suggestions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-foreground">{stats.suggestions.total}</p>
              <div className="mt-2 space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-yellow-400">Pending</span>
                  <span className="text-yellow-400">{stats.suggestions.pending}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-emerald-400">Implemented</span>
                  <span className="text-emerald-400">{stats.suggestions.implemented}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Feedback Card */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Star className="w-4 h-4 text-yellow-400" />
                User Feedback
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-foreground">{stats.feedback.average_rating.toFixed(1)}</p>
              <div className="mt-2 space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Total</span>
                  <span className="text-foreground">{stats.feedback.total}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-emerald-400">Positive</span>
                  <span className="text-emerald-400">{stats.feedback.positive}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-red-400">Negative</span>
                  <span className="text-red-400">{stats.feedback.negative}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* How it Works */}
          <Card className="md:col-span-2 lg:col-span-4">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Zap className="w-5 h-5 text-primary" />
                How the Learning System Works
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-4 gap-4">
                <div className="p-4 rounded-lg bg-secondary/50">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center mb-2">
                    <MessageSquare className="w-4 h-4 text-primary" />
                  </div>
                  <h4 className="font-medium text-sm mb-1">1. Driver Conversations</h4>
                  <p className="text-xs text-muted-foreground">
                    Drivers ask questions via WhatsApp. AI responds using the knowledge base.
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-secondary/50">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center mb-2">
                    <Brain className="w-4 h-4 text-primary" />
                  </div>
                  <h4 className="font-medium text-sm mb-1">2. Avery Reviews</h4>
                  <p className="text-xs text-muted-foreground">
                    Avery AI analyzes conversations, identifies issues, and suggests KB improvements.
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-secondary/50">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center mb-2">
                    <CheckCircle className="w-4 h-4 text-primary" />
                  </div>
                  <h4 className="font-medium text-sm mb-1">3. Manager Approval</h4>
                  <p className="text-xs text-muted-foreground">
                    Managers review suggestions, approve or reject, and implement improvements.
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-secondary/50">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center mb-2">
                    <TrendingUp className="w-4 h-4 text-primary" />
                  </div>
                  <h4 className="font-medium text-sm mb-1">4. Continuous Learning</h4>
                  <p className="text-xs text-muted-foreground">
                    The system improves over time as more corrections and feedback are incorporated.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Avery Reviews Tab */}
      {activeTab === 'reviews' && (
        <div className="space-y-4">
          {reviews.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Brain className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm font-medium text-foreground">No reviews yet</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Click &quot;Run Avery Analysis&quot; to analyze recent conversations
                </p>
              </CardContent>
            </Card>
          ) : (
            reviews.map((review) => (
              <Card key={review.id} className={review.status === 'pending' ? 'border-primary/50' : ''}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                        <Brain className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground flex items-center gap-2">
                          Batch Analysis
                          {getStatusBadge(review.status)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {review.log_ids?.length || 0} conversations • {formatTime(review.created_at)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-yellow-400" />
                          <span className="font-bold text-foreground">{review.quality_score}/10</span>
                        </div>
                        <p className="text-xs text-muted-foreground">Quality Score</p>
                      </div>
                      <button
                        onClick={() => setExpandedReview(expandedReview === review.id ? null : review.id)}
                        className="p-1 rounded hover:bg-secondary"
                      >
                        {expandedReview === review.id ? (
                          <ChevronUp className="w-5 h-5 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-muted-foreground" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Summary */}
                  <p className="mt-3 text-sm text-muted-foreground">{review.summary}</p>

                  {/* Quick Stats */}
                  <div className="mt-3 flex gap-4">
                    <Badge variant="outline" className="bg-red-500/10 text-red-400">
                      {review.issues?.length || 0} Issues
                    </Badge>
                    <Badge variant="outline" className="bg-yellow-500/10 text-yellow-400">
                      {review.kb_suggestions?.length || 0} KB Suggestions
                    </Badge>
                  </div>

                  {/* Expanded View */}
                  {expandedReview === review.id && (
                    <div className="mt-4 pt-4 border-t border-border space-y-4">
                      {/* Issues */}
                      {review.issues?.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-red-400" />
                            Issues Found
                          </h4>
                          <div className="space-y-2">
                            {review.issues.map((issue, idx) => (
                              <div 
                                key={idx}
                                className={`p-3 rounded-lg border ${getSeverityColor(issue.severity)}`}
                              >
                                <div className="flex items-start justify-between">
                                  <div>
                                    <Badge variant="outline" className={getSeverityColor(issue.severity)}>
                                      {issue.type} • {issue.severity}
                                    </Badge>
                                    <p className="text-sm text-foreground mt-1">{issue.description}</p>
                                    {issue.suggestion && (
                                      <p className="text-xs text-muted-foreground mt-1">
                                        <strong>Suggestion:</strong> {issue.suggestion}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* KB Suggestions */}
                      {review.kb_suggestions?.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                            <Lightbulb className="w-4 h-4 text-yellow-400" />
                            Knowledge Base Suggestions
                          </h4>
                          <div className="space-y-2">
                            {review.kb_suggestions.map((suggestion, idx) => (
                              <div 
                                key={idx}
                                className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30"
                              >
                                <div className="flex items-start justify-between">
                                  <div>
                                    <Badge variant="outline" className="bg-yellow-500/10 text-yellow-400">
                                      {suggestion.action} • {suggestion.priority}
                                    </Badge>
                                    <p className="text-sm font-medium text-foreground mt-1">{suggestion.topic}</p>
                                    <p className="text-xs text-muted-foreground mt-1">{suggestion.content}</p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      {review.status === 'pending' && (
                        <div className="flex gap-2 pt-2">
                          <Button 
                            variant="outline" 
                            className="flex-1"
                            onClick={() => handleReviewAction(review.id, 'approve')}
                          >
                            <ThumbsUp className="w-4 h-4 mr-2" />
                            Approve
                          </Button>
                          <Button 
                            variant="outline" 
                            className="flex-1"
                            onClick={() => handleReviewAction(review.id, 'mark_reviewed')}
                          >
                            <Check className="w-4 h-4 mr-2" />
                            Mark Reviewed
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* KB Suggestions Tab */}
      {activeTab === 'suggestions' && (
        <div className="space-y-4">
          {/* Add Suggestion Button */}
          <div className="flex justify-end">
            <Dialog open={newSuggestionOpen} onOpenChange={setNewSuggestionOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Suggestion
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create KB Suggestion</DialogTitle>
                  <DialogDescription>
                    Add a manual suggestion to improve the knowledge base
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <label className="text-sm font-medium">Topic</label>
                    <Input 
                      value={newSuggestion.topic}
                      onChange={(e) => setNewSuggestion({ ...newSuggestion, topic: e.target.value })}
                      placeholder="e.g., Safety procedures for winter driving"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Content</label>
                    <Textarea 
                      value={newSuggestion.content}
                      onChange={(e) => setNewSuggestion({ ...newSuggestion, content: e.target.value })}
                      placeholder="Describe the information to add or update..."
                      rows={4}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium">Action</label>
                      <select 
                        className="w-full mt-1 p-2 rounded-lg bg-secondary border border-border"
                        value={newSuggestion.action}
                        onChange={(e) => setNewSuggestion({ ...newSuggestion, action: e.target.value })}
                      >
                        <option value="add">Add New</option>
                        <option value="update">Update Existing</option>
                        <option value="clarify">Clarify</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Priority</label>
                      <select 
                        className="w-full mt-1 p-2 rounded-lg bg-secondary border border-border"
                        value={newSuggestion.priority}
                        onChange={(e) => setNewSuggestion({ ...newSuggestion, priority: e.target.value })}
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                    </div>
                  </div>
                  <Button className="w-full" onClick={createSuggestion}>
                    Create Suggestion
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {suggestions.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Lightbulb className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm font-medium text-foreground">No suggestions yet</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Suggestions will appear here from Avery reviews and manual entries
                </p>
              </CardContent>
            </Card>
          ) : (
            suggestions.map((suggestion) => (
              <Card key={suggestion.id} className={suggestion.status === 'pending' ? 'border-yellow-500/50' : ''}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        suggestion.source === 'avery' 
                          ? 'bg-primary/20' 
                          : suggestion.source === 'correction'
                            ? 'bg-orange-500/20'
                            : 'bg-emerald-500/20'
                      }`}>
                        {suggestion.source === 'avery' ? (
                          <Brain className="w-5 h-5 text-primary" />
                        ) : suggestion.source === 'correction' ? (
                          <AlertTriangle className="w-5 h-5 text-orange-400" />
                        ) : (
                          <BookOpen className="w-5 h-5 text-emerald-400" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{suggestion.topic}</p>
                        <p className="text-xs text-muted-foreground">
                          {suggestion.source === 'avery' ? 'From Avery' : suggestion.source === 'correction' ? 'From Correction' : 'Manual Entry'} • {formatTime(suggestion.created_at)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={getSeverityColor(suggestion.priority)}>
                        {suggestion.priority}
                      </Badge>
                      {getStatusBadge(suggestion.status)}
                    </div>
                  </div>

                  <p className="mt-3 text-sm text-muted-foreground">{suggestion.content}</p>

                  <div className="mt-3 flex items-center gap-2">
                    <Badge variant="outline">{suggestion.action}</Badge>
                  </div>

                  {/* Actions */}
                  {suggestion.status === 'pending' && (
                    <div className="mt-4 pt-3 border-t border-border flex gap-2">
                      <Button 
                        variant="default" 
                        size="sm"
                        onClick={() => handleSuggestionAction(suggestion.id, 'implement')}
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Implement
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleSuggestionAction(suggestion.id, 'approve')}
                      >
                        <ThumbsUp className="w-4 h-4 mr-1" />
                        Approve
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleSuggestionAction(suggestion.id, 'reject')}
                      >
                        <X className="w-4 h-4 mr-1" />
                        Reject
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}
    </div>
  );
}
